import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Home as HomeIcon, ChevronDown, ChevronUp, Info, Plus, Trash2} from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}
function fmtPct(n: number) {
  return n.toFixed(3) + "%"
}

// Months (1-based)
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
]

// ─── IRS Mid-Month Convention ─────────────────────────────────────────────────
// For a property placed in service in month M (1–12), the first-year deduction
// covers (12 − M + 0.5) months out of 12.
// Formula: first-year months = 12 − M + 0.5
// Annual rate = 100 / recoveryPeriod
// Year-1 deduction = (annualRate) × (firstMonths / 12)
function buildRealPropertySchedule(
  basis: number,
  recoveryYears: number,        // 27.5 or 39
  placedInServiceMonth: number, // 1–12
): ScheduleRow[] {
  const annualDepr   = basis / recoveryYears
  const firstMonths  = 12 - placedInServiceMonth + 0.5
  const yr1Fraction  = firstMonths / 12
  const yr1Depr      = annualDepr * yr1Fraction

  // Final-year months = 12 − firstMonths (complement)
  const lastFraction = 1 - yr1Fraction
  const lastDepr     = annualDepr * lastFraction

  const rows: ScheduleRow[] = []
  let accumulated = 0

  // Year 1 (partial)
  accumulated += yr1Depr
  rows.push({
    year: 1,
    beginningValue: basis,
    depreciation: yr1Depr,
    accumulatedDepreciation: accumulated,
    endingBookValue: Math.max(0, basis - accumulated),
  })

  // Full middle years
  const fullYears = Math.floor(recoveryYears) - 1
  for (let yr = 2; yr <= fullYears; yr++) {
    const begin = rows[rows.length - 1].endingBookValue
    accumulated += annualDepr
    rows.push({
      year: yr,
      beginningValue: begin,
      depreciation: annualDepr,
      accumulatedDepreciation: accumulated,
      endingBookValue: Math.max(0, basis - accumulated),
    })
  }

  // Final partial year (only if there is remaining basis)
  const remaining = basis - accumulated
  if (remaining > 0.005) {
    rows.push({
      year: rows.length + 1,
      beginningValue: remaining,
      depreciation: Math.min(lastDepr, remaining),
      accumulatedDepreciation: basis,
      endingBookValue: 0,
    })
  }

  return rows
}

// ─── Improvement entry ────────────────────────────────────────────────────────
interface Improvement {
  id: number
  description: string
  cost: string
  year: string
  month: string
  type: "residential" | "commercial"
}

let nextId = 1

// ─── FAQ ─────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "How do you calculate rental property depreciation?",
    a: "Rental property depreciation = (Purchase Price − Land Value) ÷ Recovery Period. The IRS requires the mid-month convention: in the first year you claim a partial deduction based on the month placed in service. For residential rental property the recovery period is 27.5 years; for commercial (nonresidential) property it is 39 years.",
  },
  {
    q: "Why can't land be depreciated?",
    a: "Land has an unlimited useful life — it does not wear out, get used up, or become obsolete. Only the building structure and improvements depreciate. To determine the land vs. building split, use the county tax assessment allocation, an appraisal, or the purchase contract if it specifies separately.",
  },
  {
    q: "What is the IRS mid-month convention for real property?",
    a: "For residential rental (27.5-yr) and nonresidential (39-yr) real property, the IRS uses the mid-month convention: the asset is treated as placed in service on the 15th of the month, regardless of the actual date. A property placed in service in March, for example, gets 9.5 months of depreciation in Year 1 (3.5% or 2.4% of cost depending on recovery period).",
  },
  {
    q: "What is depreciation recapture on rental property?",
    a: "When you sell a rental property, the IRS taxes previously claimed depreciation as ordinary income (up to 25% rate) under IRC Section 1250. This is called 'unrecaptured Section 1250 gain.' The total recapture equals all depreciation you claimed over the years, not just recent deductions. A 1031 like-kind exchange can defer this tax.",
  },
  {
    q: "Can I depreciate a property I converted from personal use?",
    a: "Yes. When you convert a personal residence to a rental, the depreciable basis is the lesser of (1) the FMV on the conversion date or (2) your adjusted cost basis. You begin depreciation in the month of conversion.",
  },
  {
    q: "What is cost segregation for rental property?",
    a: "Cost segregation is an IRS-recognized engineering study that reclassifies building components into shorter-life MACRS classes (5-year, 7-year, 15-year). Items like carpeting, appliances, landscaping, and parking lots can often be reclassified from 27.5/39-year straight-line to 5 or 7-year accelerated depreciation, dramatically increasing early-year deductions.",
  },
  {
    q: "How does rental depreciation affect Schedule E?",
    a: "Rental property depreciation is reported on Schedule E (Form 1040), Part I. It reduces net rental income (or increases a rental loss). If you have a net rental loss, it is subject to the passive activity loss rules: landlords with AGI under $100,000 may deduct up to $25,000 annually; the deduction phases out between $100,000 and $150,000 AGI.",
  },
  {
    q: "Do improvements to rental property get depreciated separately?",
    a: "Yes. Improvements (e.g., a new roof, HVAC system, or addition) that add value, extend the property's life, or adapt it to a new use must be capitalized and depreciated separately — not expensed. Each improvement starts a new depreciation schedule using the same recovery period as the underlying property (27.5 or 39 years) from the date placed in service.",
  },
]

// ─── Component ────────────────────────────────────────────────────────────────
export default function RentalPropertyCalculator() {
  // Primary property inputs
  const [purchasePrice, setPurchasePrice]     = useState("")
  const [landValue, setLandValue]             = useState("")
  const [propertyType, setPropertyType]       = useState<"residential" | "commercial">("residential")
  const [placedMonth, setPlacedMonth]         = useState("1")
  const [placedYear, setPlacedYear]           = useState(new Date().getFullYear().toString())

  // Optional: improvements
  const [improvements, setImprovements]       = useState<Improvement[]>([])
  const [showImprovements, setShowImprovements] = useState(false)

  // Results
  const [schedule, setSchedule]               = useState<ScheduleRow[]>([])
  const [results, setResults]                 = useState<{
    annualDepr: number; yr1Depr: number; depBasis: number; life: number
    landPct: number; buildingPct: number
  } | null>(null)
  const [error, setError]                     = useState("")

  // Recapture estimator
  const [salePrice, setSalePrice]             = useState("")
  const [yearsOwned, setYearsOwned]           = useState("")
  const [recaptureResult, setRecaptureResult] = useState<{
    totalDepr: number; recaptureTax: number; capitalGain: number
  } | null>(null)

  // FAQ
  const [openFaq, setOpenFaq]                 = useState<number | null>(null)

  const recoveryPeriod = propertyType === "residential" ? 27.5 : 39

  // ── Improvement helpers ──
  function addImprovement() {
    setImprovements((prev) => [
      ...prev,
      { id: nextId++, description: "", cost: "", year: placedYear, month: "1", type: propertyType },
    ])
  }
  function removeImprovement(id: number) {
    setImprovements((prev) => prev.filter((i) => i.id !== id))
  }
  function updateImprovement(id: number, field: keyof Improvement, value: string) {
    setImprovements((prev) =>
      prev.map((imp) => (imp.id === id ? { ...imp, [field]: value } : imp))
    )
  }

  // ── Main calculation ──
  function calculate() {
    setError("")
    const price = parseFloat(purchasePrice)
    const land  = parseFloat(landValue)
    const month = parseInt(placedMonth)

    if (isNaN(price) || price <= 0)     { setError("Please enter a valid purchase price greater than $0."); return }
    if (isNaN(land) || land < 0)        { setError("Please enter a valid land value (0 or more)."); return }
    if (land >= price)                  { setError("Land value must be less than the purchase price."); return }
    if (month < 1 || month > 12)        { setError("Please select the month the property was placed in service."); return }

    const basis     = price - land
    const annual    = basis / recoveryPeriod
    const firstMths = 12 - month + 0.5
    const yr1Depr   = annual * (firstMths / 12)

    const rows = buildRealPropertySchedule(basis, recoveryPeriod, month)
    setSchedule(rows)
    setResults({
      annualDepr: annual,
      yr1Depr,
      depBasis: basis,
      life: recoveryPeriod,
      landPct: (land / price) * 100,
      buildingPct: (basis / price) * 100,
    })
  }

  function handleExport() {
    exportToCSV(schedule, "rental-property-depreciation-schedule.csv")
  }

  // ── Recapture estimator ──
  function calcRecapture() {
    const sp  = parseFloat(salePrice)
    const yrs = parseFloat(yearsOwned)
    const price = parseFloat(purchasePrice)
    const land  = parseFloat(landValue) || 0
    if (isNaN(sp) || isNaN(yrs) || isNaN(price)) return
    const basis     = price - land
    const annual    = basis / recoveryPeriod
    const totalDepr = Math.min(annual * yrs, basis)
    const recaptureTax = totalDepr * 0.25
    const capitalGain  = Math.max(0, sp - (price - totalDepr))
    setRecaptureResult({ totalDepr, recaptureTax, capitalGain })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ── Page header ── */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-cyan-600 rounded-lg p-2">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-cyan-600 uppercase tracking-wide">
              Real Estate Depreciation — IRS MACRS
            </span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Rental Property &amp; Real Estate Depreciation Calculator
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Calculate IRS MACRS depreciation for residential rental (27.5-year) and commercial
            (39-year) property with the correct mid-month convention, partial first-year deduction,
            and improvements tracking — all exportable for Schedule E and Form 4562.
          </p>
        </div>

        {/* ── Calculator card ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Property Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">

            {/* Purchase Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Purchase Price ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number" min="0" value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                placeholder="e.g. 350,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Land Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Land Value ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number" min="0" value={landValue}
                onChange={(e) => setLandValue(e.target.value)}
                placeholder="e.g. 60,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <p className="text-xs text-gray-500 mt-1">Land is never depreciable (IRS rule)</p>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Type
              </label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value as "residential" | "commercial")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-gray-900"
              >
                <option value="residential">Residential Rental — 27.5 Years</option>
                <option value="commercial">Commercial / Nonresidential — 39 Years</option>
              </select>
            </div>

            {/* Month Placed in Service */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Month Placed in Service
              </label>
              <select
                value={placedMonth}
                onChange={(e) => setPlacedMonth(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-gray-900"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Affects partial-year first deduction (mid-month convention)</p>
            </div>

            {/* Year Placed in Service */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Placed in Service
              </label>
              <input
                type="number" min="1986" max="2100" value={placedYear}
                onChange={(e) => setPlacedYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Live basis preview */}
            {purchasePrice && landValue && parseFloat(purchasePrice) > parseFloat(landValue) ? (
              <div className="bg-cyan-50 rounded-xl p-4 flex flex-col justify-center">
                <p className="text-xs text-cyan-600 font-medium mb-1">Depreciable Basis Preview</p>
                <p className="text-xl font-bold text-cyan-700">
                  {fmt(parseFloat(purchasePrice) - parseFloat(landValue))}
                </p>
                <p className="text-xs text-gray-500">
                  ÷ {recoveryPeriod} yrs ≈ {fmt((parseFloat(purchasePrice) - parseFloat(landValue)) / recoveryPeriod)}/yr
                </p>
              </div>
            ) : null}
          </div>

          {/* Error */}
          {error ? (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-red-700 text-sm">
              <Info className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          ) : null}

          <button
            onClick={calculate}
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-sm"
          >
            Calculate Property Depreciation
          </button>

          {/* ── Results ── */}
          {results !== null ? (
            <div className="mt-8">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="bg-cyan-600 text-white rounded-xl p-4 text-center col-span-2 sm:col-span-1">
                  <div className="text-2xl font-bold">{fmt(results.yr1Depr)}</div>
                  <div className="text-cyan-100 text-xs mt-1 leading-tight">
                    Year 1 Deduction<br />
                    <span className="opacity-75">({MONTH_NAMES[parseInt(placedMonth) - 1]} placement)</span>
                  </div>
                </div>
                <div className="bg-cyan-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-cyan-700">{fmt(results.annualDepr)}</div>
                  <div className="text-gray-500 text-xs mt-1">Annual Deduction<br />(full year)</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-blue-700">{fmt(results.depBasis)}</div>
                  <div className="text-gray-500 text-xs mt-1">Depreciable Basis</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-gray-700">{results.life} yrs</div>
                  <div className="text-gray-500 text-xs mt-1">Recovery Period</div>
                </div>
              </div>

              {/* Land / building split */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Purchase Price Allocation</p>
                <div className="flex h-3 rounded-full overflow-hidden mb-2">
                  <div
                    className="bg-cyan-500 h-full"
                    style={{ width: `${results.buildingPct}%` }}
                  />
                  <div
                    className="bg-gray-300 h-full"
                    style={{ width: `${results.landPct}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-cyan-500 rounded-full" />
                    Building {results.buildingPct.toFixed(1)}% ({fmt(results.depBasis)}) — depreciable
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-gray-300 rounded-full" />
                    Land {results.landPct.toFixed(1)}% ({fmt(parseFloat(purchasePrice) - results.depBasis)}) — not depreciable
                  </span>
                </div>
              </div>

              <DepreciationScheduleTable rows={schedule} onExport={handleExport} />
            </div>
          ) : null}
        </div>

        {/* ── Improvements tracker ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-8">
          <button
            onClick={() => setShowImprovements((v) => !v)}
            className="w-full flex items-center justify-between px-8 py-5 text-left"
          >
            <div>
              <span className="text-lg font-semibold text-gray-900">
                Capital Improvements Tracker
              </span>
              <span className="ml-3 text-sm text-gray-500">(depreciated separately)</span>
            </div>
            {showImprovements
              ? <ChevronUp className="w-5 h-5 text-gray-500" />
              : <ChevronDown className="w-5 h-5 text-gray-500" />
            }
          </button>
          {showImprovements && (
            <div className="px-8 pb-8">
              <p className="text-sm text-gray-600 mb-4">
                Capital improvements (new roof, HVAC, additions) must be depreciated separately
                using the same recovery period as the underlying property. Each improvement starts
                its own mid-month convention schedule from its placed-in-service date.
              </p>
              {improvements.map((imp) => (
                <div key={imp.id} className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3 items-end">
                  <div className="col-span-2 sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                    <input
                      type="text"
                      value={imp.description}
                      onChange={(e) => updateImprovement(imp.id, "description", e.target.value)}
                      placeholder="e.g. New roof"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cost ($)</label>
                    <input
                      type="number" min="0" value={imp.cost}
                      onChange={(e) => updateImprovement(imp.id, "cost", e.target.value)}
                      placeholder="e.g. 15000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                    <select
                      value={imp.month}
                      onChange={(e) => updateImprovement(imp.id, "month", e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      {MONTH_NAMES.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                      <input
                        type="number" value={imp.year}
                        onChange={(e) => updateImprovement(imp.id, "year", e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <button
                      onClick={() => removeImprovement(imp.id)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors mb-0.5"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={addImprovement}
                className="inline-flex items-center gap-2 text-cyan-700 border border-cyan-300 hover:bg-cyan-50 rounded-lg px-4 py-2 text-sm font-medium transition-colors mt-2"
              >
                <Plus className="w-4 h-4" /> Add Improvement
              </button>

              {/* Summary of improvements */}
              {improvements.filter((i) => parseFloat(i.cost) > 0).length > 0 && (
                <div className="mt-5 bg-cyan-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-cyan-800 mb-3">Improvement Annual Depreciation Summary</p>
                  <div className="space-y-2">
                    {improvements
                      .filter((i) => parseFloat(i.cost) > 0 && i.description)
                      .map((imp) => {
                        const c  = parseFloat(imp.cost)
                        const rp = imp.type === "residential" ? 27.5 : 39
                        const mo = parseInt(imp.month)
                        const annual = c / rp
                        const yr1    = annual * ((12 - mo + 0.5) / 12)
                        return (
                          <div key={imp.id} className="flex justify-between text-sm text-cyan-900">
                            <span>{imp.description} ({imp.year})</span>
                            <span>
                              Yr 1: <strong>{fmt(yr1)}</strong> | Full yr: {fmt(annual)}
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Depreciation Recapture Estimator ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Depreciation Recapture Estimator (Section 1250)
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Estimate the tax impact when you sell — depreciation recapture is taxed at up to 25%.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Expected Sale Price ($)</label>
              <input
                type="number" min="0" value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="e.g. 500,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Years Owned</label>
              <input
                type="number" min="0" max="50" value={yearsOwned}
                onChange={(e) => setYearsOwned(e.target.value)}
                placeholder="e.g. 10"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={calcRecapture}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
              >
                Estimate Recapture Tax
              </button>
            </div>
          </div>
          {recaptureResult !== null ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <div className="text-xl font-bold text-amber-700">{fmt(recaptureResult.totalDepr)}</div>
                <div className="text-xs text-gray-600 mt-1">Accumulated Depreciation</div>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <div className="text-xl font-bold text-red-700">{fmt(recaptureResult.recaptureTax)}</div>
                <div className="text-xs text-gray-600 mt-1">Est. Recapture Tax (25%)</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-xl font-bold text-green-700">{fmt(recaptureResult.capitalGain)}</div>
                <div className="text-xs text-gray-600 mt-1">Estimated Capital Gain</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* ══════════════════════ SEO CONTENT ══════════════════════ */}
        <div className="space-y-12">

          {/* ── Formula block ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How to Calculate Rental Property Depreciation — The Formula
            </h2>
            <p className="text-gray-700 mb-4">
              The IRS requires rental property to be depreciated using the <strong>straight-line
              method</strong> under MACRS over either <strong>27.5 years</strong> (residential rental)
              or <strong>39 years</strong> (commercial). No salvage value is assumed — the full
              depreciable basis is written off over the recovery period.
            </p>
            <div className="bg-cyan-50 border-l-4 border-cyan-600 rounded-r-xl p-6 mb-5">
              <p className="font-semibold text-cyan-900 text-sm mb-2">Rental Property Depreciation Formula</p>
              <p className="font-mono text-cyan-800 text-sm leading-loose">
                Depreciable Basis = Purchase Price − Land Value<br />
                Annual Depreciation = Depreciable Basis ÷ Recovery Period<br />
                Year 1 Deduction = Annual Depr × ((12 − Month + 0.5) ÷ 12)  <span className="text-cyan-600">← mid-month convention</span><br />
                Accumulated Depreciation (Year n) = Σ Annual Deductions Years 1–n<br />
                Ending Book Value = Depreciable Basis − Accumulated Depreciation
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 font-mono text-sm leading-loose text-gray-800">
              <span className="text-gray-400">// Example: $350,000 property, $60,000 land, residential, placed in service March</span><br />
              <span>Depreciable Basis = $350,000 − $60,000 = </span><span className="text-cyan-700 font-bold">$290,000</span><br />
              <span>Annual Depreciation = $290,000 ÷ 27.5 = </span><span className="text-cyan-700 font-bold">$10,545.45/yr</span><br />
              <span>Year 1 (March) = $10,545.45 × (12 − 3 + 0.5) ÷ 12 = $10,545.45 × 0.7917 = </span>
              <span className="text-green-700 font-bold">$8,348.48</span>
            </div>
          </section>

          {/* ── IRS rules table ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Real Property Depreciation Rules — Rates &amp; Recovery Periods
            </h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-5">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Property Type</th>
                    <th className="px-4 py-3 text-right font-semibold">GDS Life</th>
                    <th className="px-4 py-3 text-right font-semibold">ADS Life</th>
                    <th className="px-4 py-3 text-right font-semibold">Annual Rate</th>
                    <th className="px-4 py-3 text-left font-semibold">Method</th>
                    <th className="px-4 py-3 text-left font-semibold">Examples</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { type: "Residential Rental",     gds: "27.5 yrs", ads: "30 yrs", rate: fmtPct(100/27.5), method: "Straight Line", ex: "Single-family rentals, apartments, condos" },
                    { type: "Nonresidential (Commercial)", gds: "39 yrs", ads: "40 yrs", rate: fmtPct(100/39), method: "Straight Line", ex: "Office buildings, retail, warehouses" },
                    { type: "Qualified Improvement Property (QIP)", gds: "15 yrs", ads: "20 yrs", rate: "6.667%", method: "150% DB / SL", ex: "Interior improvements to nonresidential buildings" },
                    { type: "Land Improvements",      gds: "15 yrs", ads: "20 yrs", rate: "5.000%", method: "150% DB / SL", ex: "Landscaping, parking lots, fences" },
                    { type: "Residential Appliances / Carpeting", gds: "5 yrs", ads: "9 yrs", rate: "20.000%", method: "200% DB / SL", ex: "Stoves, refrigerators, carpet (cost segregation)" },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3 font-medium text-cyan-700">{row.type}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.gds}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{row.ads}</td>
                      <td className="px-4 py-3 text-right font-mono text-green-700">{row.rate}</td>
                      <td className="px-4 py-3 text-gray-600">{row.method}</td>
                      <td className="px-4 py-3 text-gray-500">{row.ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">
              Source: IRS Publication 946. QIP is eligible for 100% bonus depreciation (subject to TCJA phase-down).
              Always consult a tax professional for cost segregation studies.
            </p>
          </section>

          {/* ── Tax benefits ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Real Estate Depreciation Tax Benefits
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  title: "Depreciation Tax Shield",
                  color: "blue",
                  desc: "Each year's depreciation deduction reduces your taxable rental income by the same amount, creating a tax shield even when the property generates positive cash flow. At a 32% marginal rate, a $10,545 deduction saves $3,374 in taxes annually.",
                },
                {
                  title: "Cost Segregation Study",
                  color: "green",
                  desc: "An IRS-approved engineering analysis reclassifies interior fixtures, carpeting, appliances, and site improvements into 5-, 7-, and 15-year classes. This accelerates depreciation significantly in early years — often producing 5× the first-year deduction of standard 27.5-year depreciation.",
                },
                {
                  title: "Passive Activity Loss Rules",
                  color: "cyan",
                  desc: "Rental depreciation is a passive loss. Active-participation landlords with AGI under $100,000 may deduct up to $25,000 of rental losses against ordinary income. The allowance phases out between $100,000 and $150,000 AGI. Real estate professionals (750+ hours/year) have no passive loss limit.",
                },
                {
                  title: "1031 Exchange — Defer Recapture",
                  color: "amber",
                  desc: "A Section 1031 like-kind exchange lets you sell one investment property and acquire another without triggering depreciation recapture or capital gains tax. You carry forward the accumulated depreciation to the replacement property, deferring the Section 1250 tax indefinitely.",
                },
              ].map((item) => (
                <div key={item.title} className={`rounded-xl border p-5 ${
                  item.color === "blue"  ? "border-blue-200 bg-blue-50"  :
                  item.color === "green" ? "border-green-200 bg-green-50" :
                  item.color === "cyan"  ? "border-cyan-200 bg-cyan-50"  :
                  "border-amber-200 bg-amber-50"
                }`}>
                  <h3 className={`font-semibold mb-2 ${
                    item.color === "blue"  ? "text-blue-900"  :
                    item.color === "green" ? "text-green-900" :
                    item.color === "cyan"  ? "text-cyan-900"  :
                    "text-amber-900"
                  }`}>{item.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Schedule E & Form 4562 ── */}
          <section>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-7">
              <h2 className="text-xl font-bold text-amber-900 mb-3">
                Schedule E &amp; IRS Form 4562 — Reporting Requirements
              </h2>
              <p className="text-amber-800 text-sm mb-4">
                Rental property owners must report depreciation on two IRS forms annually:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-2">Schedule E (Form 1040)</h3>
                  <p className="text-sm text-amber-800">
                    Report all rental income and expenses including depreciation on Schedule E.
                    Line 18 is specifically for depreciation expense. Net profit or loss flows
                    to Form 1040 Line 5.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-2">Form 4562</h3>
                  <p className="text-sm text-amber-800">
                    Required in the first year the property is placed in service, and any year
                    you add improvements or claim Section 179 or bonus depreciation.
                    Part III covers MACRS deductions for current and prior-year assets.
                  </p>
                </div>
              </div>
              <p className="text-amber-700 text-xs mt-4">
                ⚠️ Depreciation Hub provides educational calculation tools only. Consult a licensed
                CPA or tax attorney for filing guidance and depreciation strategy.
              </p>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Rental Property Depreciation — Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-700 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </Layout>
  )
}
